import { useTranslation } from 'next-i18next';
import { Card, Anchor } from '@components/common';

interface JobSeoWidgetProps {
  seoCities?: ApiSchema<'SeoItem'>[];
  seoDomains?: ApiSchema<'SeoItem'>[];
  seoIsAbroad?: boolean;
  className?: string;
}

/**
 * @description JobSeoWidget Component.
 * It is used for rendering SEO-related information for job listing or job detail page, including cities, domains, and whether the job location is abroad.
 *
 * - **seoCities**: ApiSchema<'SeoItem'>[] - An array of objects with the related cities for SEO purposes.
 * - **seoDomains**: ApiSchema<'SeoItem'>[] - An array of objects with the related domains for SEO purposes.
 * - **seoIsAbroad**: boolean - A boolean indicating whether the job location is abroad.
 * - **className** - string - Additional classnames, for styling purposes, optional.
 */
export const JobSeoWidget = (props: JobSeoWidgetProps) => {
  // Destructure props
  const {
    seoCities,
    seoDomains,
    seoIsAbroad,
    className = '',
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Render component
  // *****************************************************
  return (
    <Card className={`my-6 rounded-md bg-surface ${className}`}>
      <Card.Body>
        <h2 className="mb-2 text-sm font-semibold">
          {seoIsAbroad ? (
            <Anchor styling="none" href="app_job_canonical_city" symfonyRoute symfonyParams={{ location: t('global.location.abroad') }}>
              {t('job.seo_widget.title.abroad')}
            </Anchor>
          ) : t('job.seo_widget.title')}
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="grid grid-cols-1 items-start justify-items-start gap-1 sm:grid-cols-2">
            {seoCities && seoCities.map((city) => {
              if (!city.name || !city.slug) {
                return null;
              }
              return (
                <Anchor
                  key={city.id}
                  styling="text"
                  href="app_job_canonical_city"
                  symfonyRoute
                  symfonyParams={{ location: city.slug }}
                  size="xs"
                  className="!text-xs"
                >
                  { city.name }
                </Anchor>
              );
            })}
          </div>
          <div className="grid grid-cols-1 items-start justify-items-start gap-1 sm:grid-cols-2">
            {seoDomains && seoDomains.map((domain) => {
              if (!domain.name || !domain.slug) {
                return null;
              }
              return (
                <Anchor
                  key={domain.id}
                  styling="text"
                  href="app_job_canonical"
                  symfonyRoute
                  symfonyParams={{ params: domain.slug }}
                  size="xs"
                  className="!text-xs"
                >
                  { domain.name }
                </Anchor>
              );
            })}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
